import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import {
  processFitNeedsRepublish,
  processFitRows,
} from "@/components/projects/diagnostic/diagnosticData";
import { FilterButton } from "@/components/projects/diagnostic/orgStructure/FilterButton";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import { useDiagnosticInScopeItems } from "@/hooks/useDiagnosticScopeItems";
import { formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

import { FIT_SEGMENTS, PROCESS_LABELS, type FitColor } from "./processFitLabels";
import { scopeItemsBasis } from "./scopeItemSource";

/** O card branco das telas de detalhe, o mesmo do WRICEF e dos gaps. */
const CARD = "overflow-hidden rounded-lg border border-[#e3e8ef] bg-white";
const HEAD = "px-3 py-2 font-mono text-[9px] font-medium uppercase tracking-[0.08em] text-[#7b8799]";
const CELL = "px-3 py-2 align-top text-[12px] text-[#08254c]";

/**
 * "Aderência Fit-to-Standard por processo", o detalhe do card do Ato 2.
 *
 * Pedido do David em 19/10/2026, para o Marco saber do que o card trata: o que
 * cada cor da barra quer dizer, a conta de cada processo e os scope items do
 * escopo pela cor. O processo clicado no card chega marcado na tabela.
 *
 * Os scope items vêm de `/scope-activation/items`, pela base do tile "Scope items
 * ativos". O processo de cada um não vem nessa lista (o eixo é resolvido no
 * servidor, sobre a curadoria de áreas), então a divisão por processo fica na
 * tabela e a lista se filtra pela cor.
 *
 * A cor é o veredito, na tabela e na lista (21/10/2026): o item que o ECC não
 * decidiu e o analista ativou é Ativar. A leitura do motor não aparece aqui.
 */
export function ProcessFitDetail({ sections, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [search] = useSearchParams();
  const selected = search.get("process");
  const rows = processFitRows(sections);
  const labels = PROCESS_LABELS(t);
  const segments = FIT_SEGMENTS(t);
  const items = useDiagnosticInScopeItems(projectId, scopeItemsBasis(validation));
  const [color, setColor] = useState<FitColor | null>(null);

  const all = items.data ?? [];
  const shown = color ? all.filter((item) => item.destination === color) : all;
  const segmentOf = (verdict: string) => segments.find((segment) => segment.key === verdict);

  return (
    <div className="space-y-5">
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {t("How much of the business the GROW standard already covers")}
      </p>

      <ul data-testid="process-fit-legend" className={cn(CARD, "grid gap-3 p-4 sm:grid-cols-2")}>
        {segments.map((segment) => (
          <li key={segment.key} className="flex items-start gap-2.5">
            <span aria-hidden className={cn("mt-1 h-2.5 w-2.5 shrink-0 rounded-sm", segment.className)} />
            <span className="text-[12px] leading-snug text-[#596b85]">
              <span className="font-semibold text-[#08254c]">{segment.label}</span>
              {" · "}
              {segment.meaning}
            </span>
          </li>
        ))}
      </ul>

      {processFitNeedsRepublish(sections) && (
        <p className="text-[12px] text-[#7b8799]">
          {t("This axis needs to be recalculated: publish the calibration again.")}
        </p>
      )}

      <div className={CARD}>
        <div className="overflow-x-auto">
          <table data-testid="process-fit-table" className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
                <th className={HEAD}>{t("Process")}</th>
                <th className={cn(HEAD, "text-right")}>{t("Fit")}</th>
                <th className={cn(HEAD, "text-right")}>{t("Scope items")}</th>
                {segments.map((segment) => (
                  <th key={segment.key} className={cn(HEAD, "text-right")}>
                    <span className="inline-flex items-center gap-1.5">
                      <span aria-hidden className={cn("h-2 w-2 rounded-sm", segment.className)} />
                      {segment.label}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const current = row.process === selected;
                return (
                  <tr
                    key={row.process}
                    data-testid={`process-fit-row-${row.process}`}
                    aria-current={current ? "true" : undefined}
                    className={cn("border-b border-[#eef1f5] last:border-b-0", current && "bg-[#fdf6e7]")}
                  >
                    <td className={cn(CELL, "font-medium")}>{labels[row.process] ?? row.process}</td>
                    <td className={cn(CELL, "text-right tabular-nums")}>
                      {row.fitPct !== undefined ? `${row.fitPct}%` : NO_VALUE}
                    </td>
                    <td className={cn(CELL, "text-right tabular-nums")}>{formatCount(row.total)}</td>
                    {segments.map((segment) => (
                      <td key={segment.key} className={cn(CELL, "text-right tabular-nums")}>
                        {formatCount(row[segment.key])}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="max-w-3xl text-[11px] leading-snug text-[#7b8799]">
        {t(
          "Brazilian tax localisation is not a process: it cuts across all four, and this axis does not produce it.",
        )}
      </p>

      <section className="space-y-2">
        <h3 className="text-[13px] font-semibold text-[#08254c]">{t("The scope items, by bar colour")}</h3>
        {items.isLoading ? (
          <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>
        ) : items.isError ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]">
            {t("Could not load the scope items.")}
            <button
              type="button"
              onClick={() => void items.refetch()}
              className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
            >
              {t("Try again")}
            </button>
          </div>
        ) : (
          <div data-testid="process-fit-items" className={CARD}>
            <div className="flex flex-wrap gap-2 border-b border-[#eef1f5] px-4 py-3">
              <FilterButton active={color === null} onClick={() => setColor(null)}>
                {t("All")} {formatCount(all.length)}
              </FilterButton>
              {segments.map((segment) => (
                <FilterButton
                  key={segment.key}
                  active={color === segment.key}
                  onClick={() => setColor(segment.key)}
                >
                  {segment.label} {formatCount(all.filter((item) => item.destination === segment.key).length)}
                </FilterButton>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr className="border-b border-[#e8ebf0] bg-[#f7f9fc] text-left">
                    <th className={HEAD}>{t("Scope item")}</th>
                    <th className={HEAD}>{t("Name")}</th>
                    <th className={HEAD}>{t("LoB")}</th>
                    <th className={HEAD}>{t("Verdict")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((item) => {
                    const segment = segmentOf(item.destination);
                    return (
                      <tr
                        key={item.scope_item_id}
                        data-testid={`process-fit-item-${item.scope_item_id}`}
                        className="border-b border-[#eef1f5] last:border-b-0"
                      >
                        <td className={cn(CELL, "font-mono")}>{item.scope_item_id}</td>
                        <td className={CELL}>{item.scope_item_name ?? NO_VALUE}</td>
                        <td className={CELL}>{item.lob ?? NO_VALUE}</td>
                        <td className={CELL}>
                          <span className="inline-flex items-center gap-1.5">
                            <span
                              aria-hidden
                              className={cn("h-2 w-2 rounded-sm", segment?.className ?? "bg-[#b2bccb]")}
                            />
                            {segment?.label ?? item.destination}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
